import { RIFT_ROSTER } from '../../content/monsters/rift';
import { type V2, dist } from '../../core/math';
import { rollLoot } from '../../game/loot';
import type { ZoneOpts } from '../../game/netHooks';
import type { Actor } from '../../sim/actor';
import type { SimEvent } from '../../sim/events';
import type { StatusApply } from '../../sim/skillTypes';
import { consumeRested } from '../bounties/dayCycle';
import { ORB_DEF, type OrbKind } from './content';
import { type DeathView, type HudView, type InstanceEnv, clockText, emptyHud, once } from './env';
import { type PortalFx, portalFx } from './fx';
import { riftRested, spendRiftEnergy } from './lockouts';
import { gearChest, grantDrop, mergeDrop, scaleDrop } from './rewards';
import { RIFT } from './rules';
import { InstanceScript, type Msg, type ScriptState, nextZoneOpts, num, str } from './script';

/**
 * RIFT DELVE (D6) — the Chaos Dungeon loop (research 01 §7.2, 02 §5.5):
 * three floors sharing one purification gauge filled by kills weighted by
 * role; the portal to floor 2 opens at 20% and to floor 3 at 50%; floor 2
 * adds elites and the Rift Warden, floor 3 the breakable rift cores (each
 * spawns a mini-boss) and Voidmaw; 100% purifies the rift and pays the end
 * chest. One run timer (see RIFT.timeLimit); a timeout pays a share of the
 * chest proportional to the gauge.
 *
 * Host: wave spawner around `wave_*`, gauge, timer, portal, pickups.
 * Every peer: its own Rift Energy (spent on entry: no energy = practice),
 * Rested ×2, cooldown refunds from elite / boss kills, rewards.
 */

export type RiftPhase = 'run' | 'portal' | 'moving' | 'done' | 'timeout';

export interface RiftState extends ScriptState {
  floor: number;
  /** Purification gauge 0–100. */
  gauge: number;
  /** Seconds left on the run timer. */
  time: number;
  /** Seconds spent (for the end screen). */
  elapsed: number;
  phase: RiftPhase;
  /** Floor boss: 0 not yet, 1 fighting, 2 slain. */
  boss: number;
  /** Rift cores shattered (floor 3). */
  cores: number;
  kills: number;
  /** Where every peer goes when the phase is `moving`. */
  go: { zone: string; opts: ZoneOpts } | null;
}

export const RIFT_ZONES = ['rift_1', 'rift_2', 'rift_3'] as const;
const FLOOR_NAMES = ['Riven Hollow', 'Shattered Galleries', 'Heart of the Rift'];

export const riftFloor = (zone: string): number => Math.max(1, RIFT_ZONES.indexOf(zone as (typeof RIFT_ZONES)[number]) + 1);

/** Elite affixes (host applies the status; every peer renames the elite from its tag). */
export const AFFIXES: Record<string, { name: string; status: StatusApply }> = {
  frenzied: { name: 'Frenzied', status: { id: 'swift', duration: 1e9, value: 0.35 } },
  bulwark: { name: 'Bulwark', status: { id: 'guard', duration: 1e9, value: 0.35 } },
  vicious: { name: 'Vicious', status: { id: 'atkUp', duration: 1e9, value: 0.35 } },
  mending: { name: 'Mending', status: { id: 'regen', duration: 1e9, value: 0.015 } },
  fleet: { name: 'Fleet', status: { id: 'haste', duration: 1e9, value: 0.45 } },
};

/** Pickup effects: short statuses on the picker (research: wind spheres; plus health / power motes). */
export const ORB_EFFECT: Record<OrbKind, { status: StatusApply; text: string; color: string }> = {
  wind: { status: { id: 'wind', duration: 12 }, text: 'Tailwind — move speed +40%', color: '#7ad8ff' },
  health: { status: { id: 'regen', duration: 5, value: 0.07 }, text: 'Mending Mote — health restored', color: '#7bd66b' },
  power: { status: { id: 'atkUp', duration: 15, value: 0.25 }, text: 'Fury Mote — attack +25%', color: '#ff9a5a' },
};

/** Gauge worth of a slain rift monster. */
export function riftWeight(a: Actor): number {
  const m = a.monster;
  if (!m) return 0;
  const w = RIFT.weights;
  if (m.tags.includes('rift:boss')) return w.boss;
  if (m.tags.includes('rift:core')) return w.core;
  if (m.tags.includes('rift:mini')) return w.mini;
  if (m.tags.includes('rift:elite')) return w.elite;
  if (m.tags.includes('rift:trash')) return m.grade === 'minion' ? w.minion : w.normal;
  return 0;
}

/** Cuts the remaining cooldowns of a player's skills (battle items excluded); 1 = full reset. */
export function cutCooldowns(a: Actor, frac: number): void {
  for (const [id, t] of [...a.cooldowns]) {
    if (id.startsWith('bi_')) continue;
    if (frac >= 1) a.cooldowns.delete(id);
    else a.cooldowns.set(id, t * (1 - frac));
  }
}

export function initialRiftState(zone: string, opts: ZoneOpts): RiftState {
  return {
    seq: 0,
    floor: riftFloor(zone),
    gauge: Math.max(0, Math.min(100, num(opts.rg, 0))),
    time: num(opts.rt, RIFT.timeLimit),
    elapsed: num(opts.re, 0),
    phase: 'run',
    boss: 0,
    cores: 0,
    kills: num(opts.rk, 0),
    go: null,
  };
}

export class RiftScript extends InstanceScript<RiftState> {
  private packT = 1;
  private eliteT = 0;
  private orbT = RIFT.orbEvery;
  private portal: PortalFx | null = null;
  private leaving = false;

  constructor(env: InstanceEnv, opts: ZoneOpts, zone: string) {
    super(env, opts, initialRiftState(zone, opts));
  }

  get floor(): number {
    return this.state.floor;
  }

  private get paid(): boolean {
    return this.env.memo.get(`${this.run}:paid`) === true;
  }

  // ---------------------------------------------------------------- lifecycle

  protected override init(): void {
    const p = this.env.progression;
    // Rift Energy is spent on entry (leaving early forfeits it); every member pays their own.
    if (this.floor === 1 && p && once(this.env, `${this.run}:energy`)) {
      const paid = spendRiftEnergy(p.char, p.account);
      this.env.memo.set(`${this.run}:paid`, paid);
      if (paid) this.env.toast(`Rift Energy −${RIFT.energyCost}`, '#d070ff');
      else this.env.toast('Not enough Rift Energy: practice run, no rewards', '#e5a24d');
      this.env.save();
    }
  }

  protected override hostInit(): void {
    this.eliteT = RIFT.eliteFirst[this.floor - 1] ?? 20;
    this.spawnCores();
  }

  /** Floor 3: the five rift cores (unless a previous host already placed them). */
  private spawnCores(): void {
    if (this.floor !== 3 || this.state.cores > 0 || this.tagged('rift:core', true).length > 0) return;
    for (let i = 0; i < 5; i++) {
      const sp = this.z.layout.spawnPoints[`core_${i}`];
      if (sp) this.z.spawnMonster(RIFT_ROSTER.core, RIFT.level, { x: sp.x, z: sp.z }, { facing: sp.facing ?? 0, tags: ['rift', 'rift:core'] });
    }
  }

  protected override becameHost(): void {
    // Monsters were adopted with their tags; restart the spawn timers and re-apply host-side tuning.
    this.packT = 0.5;
    this.eliteT = Math.min(this.eliteT || 10, 10);
    for (const a of [...this.tagged('rift:elite'), ...this.tagged('rift:mini')]) this.hunt(a);
    // The expected host never arrived: this peer sets the floor up itself.
    this.spawnCores();
  }

  /** Horde elites hunt the party instead of guarding a post. */
  private hunt(a: Actor): void {
    const m = a.monster;
    if (!m) return;
    m.aggroRange = 30;
    m.leash = 90;
  }

  protected override disposed(): void {
    this.portal = null;
  }

  // ---------------------------------------------------------------- host

  protected override hostTick(dt: number): void {
    const s = this.state;
    if (s.phase === 'run' || s.phase === 'portal') {
      s.time -= dt;
      s.elapsed += dt;
      if (s.time <= 0) {
        s.time = 0;
        this.finish('timeout');
        return;
      }
      this.spawner(dt);
      this.bosses();
      this.pickups(dt);
      if (s.phase === 'portal') this.checkPortal();
    }
  }

  protected override mirrorTick(dt: number): void {
    const s = this.state;
    if (s.phase === 'run' || s.phase === 'portal') {
      s.time = Math.max(0, s.time - dt);
      s.elapsed += dt;
    }
  }

  private population(): number {
    const base = RIFT.population[this.floor - 1] ?? 14;
    return base + RIFT.perExtraPlayer * Math.max(0, this.world.partySize - 1);
  }

  private spawner(dt: number): void {
    this.packT -= dt;
    if (this.packT <= 0) {
      this.packT = RIFT.packEvery;
      if (this.tagged('rift:trash').length < this.population()) this.spawnPack();
    }
    this.eliteT -= dt;
    if (this.eliteT <= 0) {
      this.eliteT = RIFT.eliteEvery[this.floor - 1] ?? 24;
      if (this.tagged('rift:elite').length < RIFT.maxElites) {
        const pt = this.wavePoint();
        if (pt) this.spawnElite(this.jitter(pt, 2), 'rift:elite');
      }
    }
  }

  /** A wave point 8–34 m from the nearest living player (else the closest ones). */
  wavePoint(): V2 | null {
    const pts: V2[] = [];
    for (let i = 0; i < 8; i++) {
      const sp = this.z.layout.spawnPoints[`wave_${i}`];
      if (sp) pts.push(sp);
    }
    if (!pts.length) return null;
    const living = this.livingPlayers();
    const rng = this.world.rng;
    if (!living.length) return rng.pick(pts);
    const scored = pts.map((p) => ({ p, d: Math.min(...living.map((a) => dist(p, a.pos))) }));
    const good = scored.filter((x) => x.d >= 8 && x.d <= 34);
    const pool = good.length ? good : scored.sort((a, b) => a.d - b.d).slice(0, 3);
    return rng.pick(pool).p;
  }

  private jitter(p: V2, r: number): V2 {
    const rng = this.world.rng;
    const a = rng.range(0, Math.PI * 2);
    const d = rng.range(0, r);
    const q = { x: p.x + Math.sin(a) * d, z: p.z + Math.cos(a) * d };
    return this.z.nav.walkable(q) ? q : (this.z.nav.nearestWalkable(q, 6) ?? { ...p });
  }

  private spawnPack(): void {
    const pt = this.wavePoint();
    if (!pt) return;
    const rng = this.world.rng;
    const [lo, hi] = RIFT.packSize[this.floor - 1] ?? [4, 6];
    const [imp = 'rift_imp', hound = 'rift_hound', caster = 'rift_caster'] = RIFT_ROSTER.swarm;
    const mix = [
      [5, 3, 1],
      [4, 3, 3],
      [3, 3, 3],
    ][this.floor - 1] ?? [3, 3, 3];
    const n = rng.int(lo, hi);
    for (let i = 0; i < n; i++) {
      const def = rng.weighted([
        { weight: mix[0] ?? 1, value: imp },
        { weight: mix[1] ?? 1, value: hound },
        { weight: mix[2] ?? 1, value: caster },
      ]);
      const p = this.jitter(pt, 3.5);
      this.z.spawnMonster(def, RIFT.level, p, { facing: rng.range(-Math.PI, Math.PI), tags: ['rift', 'rift:trash'] });
    }
  }

  private spawnElite(at: V2, role: 'rift:elite' | 'rift:mini', hpMult = 1): Actor {
    const rng = this.world.rng;
    const affix = rng.pick(Object.keys(AFFIXES));
    const def = rng.pick(RIFT_ROSTER.elites);
    const a = this.z.spawnMonster(def, RIFT.level, at, { facing: rng.range(-Math.PI, Math.PI), tags: ['rift', role, `affix:${affix}`], hpMult });
    this.hunt(a);
    const fx = AFFIXES[affix];
    if (fx) this.world.applyStatusTo(a, fx.status, a.id);
    this.nameAffixed(a);
    return a;
  }

  private bosses(): void {
    const s = this.state;
    if (s.boss !== 0) return;
    const due = this.floor === 2 ? s.gauge >= RIFT.wardenAt : this.floor === 3 ? s.cores >= RIFT.voidmawCores || s.gauge >= RIFT.voidmawAt : false;
    if (!due) return;
    const id = this.floor === 2 ? RIFT_ROSTER.bosses[0] : RIFT_ROSTER.bosses[1];
    if (!id) return;
    const sp = this.spawnPoint('boss');
    this.z.spawnMonster(id, RIFT.level, { x: sp.x, z: sp.z }, { facing: sp.facing ?? 0, tags: ['rift', 'rift:boss'] });
    this.update({ boss: 1 });
  }

  private spawnOrb(kind: OrbKind, at: V2): void {
    this.z.spawnMonster(ORB_DEF[kind], RIFT.level, this.jitter(at, 1.5), { team: 'neutral', tags: ['rift', 'rift:orb', `orb:${kind}`] });
  }

  private pickups(dt: number): void {
    this.orbT -= dt;
    if (this.orbT <= 0) {
      this.orbT = RIFT.orbEvery;
      if (this.tagged('rift:orb').length < 3) {
        const pt = this.wavePoint();
        if (pt) this.spawnOrb('wind', pt);
      }
    }
    const living = this.livingPlayers();
    for (const orb of this.tagged('rift:orb')) {
      const m = orb.monster;
      if (!m) continue;
      if (m.age > RIFT.orbLife) {
        this.world.removeActor(orb.id);
        continue;
      }
      const kind = (m.tags.find((t) => t.startsWith('orb:'))?.slice(4) ?? 'wind') as OrbKind;
      const who = living.find((p) => dist(p.pos, orb.pos) <= RIFT.pickupRadius);
      if (!who) continue;
      const fx = ORB_EFFECT[kind];
      this.world.grantStatus(who, fx.status, orb.id);
      this.world.removeActor(orb.id);
      this.toPeer(who.owner, 'pick', { k: kind });
    }
  }

  private checkPortal(): void {
    const at = this.spawnPoint('portal');
    if (!this.livingPlayers().some((p) => dist(p.pos, at) <= RIFT.portalRadius)) return;
    const next = RIFT_ZONES[this.floor];
    if (!next) return;
    const s = this.state;
    const opts = nextZoneOpts(this.opts, next, this.world.localPeer, { run: this.run, rg: Math.round(s.gauge * 100) / 100, rt: Math.round(s.time * 10) / 10, re: Math.round(s.elapsed), rk: s.kills });
    this.update({ phase: 'moving', go: { zone: next, opts } });
  }

  /** Adds purification; opens portals and ends the run at 100%. */
  addGauge(amount: number): void {
    const s = this.state;
    if (s.phase !== 'run' && s.phase !== 'portal') return;
    const cap = RIFT.floorCap[this.floor - 1] ?? 100;
    const gauge = Math.min(cap, s.gauge + amount);
    const portalAt = RIFT.portalAt[this.floor - 1];
    if (this.floor === 3 && gauge >= 100) {
      s.gauge = 100;
      this.finish('done');
      return;
    }
    if (portalAt !== undefined && s.phase === 'run' && gauge >= portalAt) this.update({ gauge, phase: 'portal' });
    else {
      s.gauge = gauge;
      this.soon();
    }
  }

  private finish(phase: 'done' | 'timeout'): void {
    for (const a of this.tagged('rift', true)) this.world.removeActor(a.id);
    this.update({ phase, go: null });
  }

  protected override hostEvent(ev: SimEvent): void {
    if (ev.type !== 'death') return;
    const a = this.world.actors.get(ev.id);
    const m = a?.monster;
    if (!a || !m?.tags.includes('rift') || m.tags.includes('rift:orb')) return;
    const s = this.state;
    s.kills++;
    if (m.tags.includes('rift:elite') || m.tags.includes('rift:mini')) {
      const rng = this.world.rng;
      this.spawnOrb(rng.chance(0.5) ? 'health' : 'power', a.pos);
      if (rng.chance(0.3)) this.spawnOrb('wind', a.pos);
    }
    if (m.tags.includes('rift:core')) {
      // Rift Surge for everyone near the shattered core, and a mini-boss climbs out.
      for (const p of this.livingPlayers()) if (dist(p.pos, a.pos) <= 20) this.world.grantStatus(p, { id: 'riftCore', duration: 15 }, a.id);
      this.spawnElite(this.jitter(a.pos, 2.5), 'rift:mini', 1.8);
      this.update({ cores: s.cores + 1 });
    }
    if (m.tags.includes('rift:boss')) this.update({ boss: 2 });
    this.addGauge(riftWeight(a));
  }

  // ---------------------------------------------------------------- every peer

  protected override notice(data: Msg): void {
    if (data.t === 'pick') {
      const fx = ORB_EFFECT[str(data.k, 'wind') as OrbKind];
      if (fx) this.env.toast(fx.text, fx.color);
    }
  }

  protected override localEvent(ev: SimEvent): void {
    if (ev.type === 'spawn') {
      const a = this.world.actors.get(ev.id);
      if (a) this.nameAffixed(a);
      return;
    }
    if (ev.type !== 'death') return;
    const a = this.world.actors.get(ev.id);
    const m = a?.monster;
    const me = this.me;
    if (!a || !m?.tags.includes('rift') || !me?.alive) return;
    if ((m.tags.includes('rift:elite') || m.tags.includes('rift:mini')) && dist(me.pos, a.pos) <= 40) {
      cutCooldowns(me, 0.4);
      this.env.toast('Elite slain — cooldowns −40%', '#ffd070');
    } else if (m.tags.includes('rift:boss') && this.floor === 2) {
      cutCooldowns(me, 1);
      this.env.toast('The Warden falls — all cooldowns reset', '#ffd070');
    }
  }

  private nameAffixed(a: Actor): void {
    const tag = a.monster?.tags.find((t) => t.startsWith('affix:'));
    const fx = tag ? AFFIXES[tag.slice(6)] : undefined;
    if (fx && !a.name.startsWith(fx.name)) a.name = `${fx.name} ${a.name}`;
  }

  protected override changed(prev: RiftState, next: RiftState): void {
    if (next.phase === prev.phase) {
      if (next.boss === 1 && prev.boss === 0) this.env.announce(this.floor === 2 ? 'The Rift Warden rises from the crystal!' : 'Voidmaw tears through the heart of the rift!', 'boss');
      if (next.cores > prev.cores) this.env.toast(`Rift Core shattered (${next.cores}/5) — Rift Surge!`, '#d070ff');
      return;
    }
    switch (next.phase) {
      case 'portal':
        this.env.announce(`A rift portal has opened — ${Math.floor(next.gauge)}% purified`, 'success');
        this.showPortal(0xb070ff);
        break;
      case 'moving': {
        const go = next.go;
        this.env.announce(`Descending to floor ${this.floor + 1}…`, 'info');
        if (go) this.later(RIFT.moveDelay, () => this.env.transition(go.zone, go.opts));
        break;
      }
      case 'done':
        this.env.announce('The rift is purified!', 'success');
        this.showPortal(0x9ad8ff);
        this.reward(true);
        break;
      case 'timeout':
        this.env.announce(`Time's up — the rift collapses at ${Math.floor(next.gauge)}%`, 'warning');
        this.showPortal(0x9ad8ff);
        this.reward(false);
        break;
      default:
        break;
    }
  }

  private showPortal(color: number): void {
    if (!this.env.visual) return;
    this.portal?.dispose();
    const fx = portalFx(this.z, this.spawnPoint('portal'), color);
    this.portal = fx;
    this.onDispose(() => fx.dispose());
  }

  protected override present(dt: number): void {
    this.portal?.update(dt);
  }

  protected override localTick(): void {
    const s = this.state;
    const me = this.me;
    if ((s.phase === 'done' || s.phase === 'timeout') && me?.alive && !this.leaving && dist(me.pos, this.spawnPoint('portal')) <= RIFT.portalRadius) {
      this.leaving = true;
      this.env.leave();
    }
  }

  /** End-of-run chest for the local character (once per run). */
  private reward(full: boolean): void {
    const p = this.env.progression;
    if (!p || !once(this.env, `${this.run}:reward`)) return;
    const s = this.state;
    const time = clockText(s.elapsed);
    if (!this.paid) {
      this.env.toast('Practice run complete — no Rift Energy was spent, so no chest', '#e5a24d');
      return;
    }
    this.env.syncPeriods();
    const rested = consumeRested(p.char, 'rift');
    const drop = mergeDrop(rollLoot('rift', { level: RIFT.level, rng: p.rng, rested }), gearChest(RIFT.level, p.rng));
    const final = full ? drop : scaleDrop(drop, s.gauge / 100);
    const sub = `${rested ? 'Rested ×2 · ' : ''}${full ? `100% in ${time}` : `${Math.floor(s.gauge)}% — time ran out`}`;
    this.env.rewards(grantDrop(p, final, 'Rift Delve', full ? 'Rift Delve — Purified' : 'Rift Delve — Collapsed', sub));
    p.char.counters.riftClears = (p.char.counters.riftClears ?? 0) + (full ? 1 : 0);
    this.env.save();
  }

  // ---------------------------------------------------------------- views

  view(): HudView {
    const s = this.state;
    const v = emptyHud('rift', 'Rift Delve', `Floor ${s.floor} — ${FLOOR_NAMES[s.floor - 1] ?? ''}`);
    v.gauge = s.gauge;
    v.marks = [...RIFT.portalAt];
    v.timer = s.phase === 'done' || s.phase === 'timeout' ? null : { label: 'Time left', seconds: s.time, warn: s.time < 60 };
    const p = this.env.progression;
    if (!this.paid) v.badges.push({ text: 'Practice', tone: 'grey' });
    else if (p && riftRested(p.char) >= 20 && s.phase !== 'done' && s.phase !== 'timeout') v.badges.push({ text: 'Rested ×2', tone: 'gold' });
    if (s.phase === 'portal') v.badges.push({ text: 'Portal open', tone: 'violet' });
    const live = s.phase === 'run' || s.phase === 'portal';
    if (s.boss === 1 && live) v.badges.push({ text: 'Floor boss', tone: 'red' });
    if (this.floor === 3) v.badges.push({ text: `Cores ${s.cores}/5`, tone: 'violet' });
    if (s.phase === 'done') v.badges.push({ text: 'Purified', tone: 'green' });
    if (s.phase === 'timeout') v.badges.push({ text: 'Collapsed', tone: 'red' });
    v.actions.push(s.phase === 'done' || s.phase === 'timeout' ? { id: 'leave', label: 'Leave', tone: 'gold' } : this.leaveAction('Leave the Rift Delve? The run ends and its Rift Energy is not refunded.'));
    return v;
  }

  override deathView(): DeathView {
    return {
      title: 'You have fallen',
      text: 'The rift pulls you back to the floor entrance. Rise and keep purifying.',
      countdown: null,
      actions: [{ id: 'respawn', label: 'Revive at the entrance', tone: 'gold' }, this.leaveAction()],
    };
  }

  override action(id: string): void {
    if (id === 'leave') this.env.leave();
    else if (id === 'respawn') {
      const sp = this.spawnPoint('start');
      this.reviveMe(1, sp);
    }
  }
}
